import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Spinner from "../components/common/Spinner";
import analytics from "../services/analytics";
import {
  colors,
  typography,
  buttons,
  form,
  surfaces,
  utils,
} from "../styles/index";

const API_BASE = process.env.REACT_APP_API_URL;

export default function TryDemoPage({ onLogin }) {
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [agreedPrivacy, setAgreedPrivacy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const navigate = useNavigate();
  const canStart = agreedTerms && agreedPrivacy;

  const handleStartDemo = async () => {
    if (!canStart || loading) return;
    setError("");
    setLoading(true);

    analytics.event("demo_start_attempt", { consent_given: true });

    try {
      const res = await axios.post(`${API_BASE}/public/demo-session`, {
        agreed_terms: true,
        agreed_privacy: true,
        source: "try-demo-page",
      });

      const token = res.data.token;
      const avatar = res.data.avatar || "🎓";

      analytics.event("demo_login_success");
      onLogin(token, avatar);
      navigate("/app");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      analytics.event('demo_start_failed', { error_status: status || 'unknown' });
      setError(
        detail ||
          "The demo environment is unavailable right now. Please try again in a moment."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: colors.bgSoft,
        padding: "48px 24px",
        ...utils.centerFlex,
        alignItems: "flex-start",
      }}
    >
      <div
        style={{
          ...surfaces.card,
          padding: 48,
          maxWidth: 600,
          width: "100%",
        }}
      >
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <Link to="/"><img src="/logo.svg" alt="SimulateJob" style={{ height: 48 }} /></Link>
        </div>

        <h1 style={{ ...typography.h2, textAlign: "center", marginBottom: 8 }}>
          Try SimulateJob
        </h1>
        <p
          style={{
            ...typography.body,
            textAlign: "center",
            color: colors.textMuted,
            marginBottom: 32,
          }}
        >
          Start a temporary workspace instantly. No account needed.
        </p>

        {/* What's included */}
        <div
          style={{
            background: colors.primarySoft,
            border: `1px solid ${colors.primary}22`,
            borderRadius: 10,
            padding: "20px 24px",
            marginBottom: 28,
          }}
        >
          <p
            style={{
              fontWeight: 600,
              marginBottom: 10,
              color: colors.textDark,
            }}
          >
            What you'll have access to:
          </p>
          <ul
            style={{
              margin: 0,
              paddingLeft: 20,
              color: colors.text,
              lineHeight: "1.9",
            }}
          >
            <li>A temporary organization, just for your session (about an hour)</li>
            <li>A sample plan - a warehouse closing-shift procedure - with its games already built to play</li>
            <li>A guided walkthrough of how a procedure becomes a game</li>
            <li>A few AI steps of your own: draft a plan from a short document or a chat</li>
          </ul>
        </div>

        {/* Real document warning */}
        <div
          style={{
            background: "#FFF7ED",
            border: `1px solid ${colors.warning || "#F59E0B"}`,
            borderRadius: 10,
            padding: "14px 20px",
            marginBottom: 28,
            display: "flex",
            gap: 10,
            alignItems: "flex-start",
          }}
        >
          <span style={{ fontSize: "1.1rem", lineHeight: 1.5 }}>⚠️</span>
          <p
            style={{
              margin: 0,
              fontSize: "0.9rem",
              color: colors.textDark,
              lineHeight: "1.6",
            }}
          >
            <strong>Do not upload confidential documents.</strong> Demo
            workspaces are deleted when the session ends; use a public or
            made-up procedure to try your own.
          </p>
        </div>

        {/* Consent checkboxes */}
        <div style={{ marginBottom: 28 }}>
          <label
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              marginBottom: 14,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={agreedTerms}
              onChange={(e) => setAgreedTerms(e.target.checked)}
              style={{ marginTop: 3, accentColor: colors.primary }}
            />
            <span style={{ ...typography.small }}>
              I have read and agree to the{" "}
              <Link to="/terms" target="_blank" rel="noopener noreferrer">
                Terms of Service
              </Link>
            </span>
          </label>

          <label
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={agreedPrivacy}
              onChange={(e) => setAgreedPrivacy(e.target.checked)}
              style={{ marginTop: 3, accentColor: colors.primary }}
            />
            <span style={{ ...typography.small }}>
              I have read and agree to the{" "}
              <Link to="/privacy" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </Link>
            </span>
          </label>
        </div>

        {error && (
          <div style={{ ...form.error, marginBottom: 16 }}>{error}</div>
        )}

        <button
          onClick={handleStartDemo}
          disabled={!canStart || loading}
          style={{
            ...buttons.primary,
            width: "100%",
            opacity: !canStart || loading ? 0.5 : 1,
            cursor: !canStart || loading ? "not-allowed" : "pointer",
          }}
        >
          {loading ? (
            <>
              <Spinner size={14} />
              Starting demo...
            </>
          ) : (
            "Start Demo"
          )}
        </button>

        <p
          style={{
            ...typography.small,
            marginTop: 20,
            textAlign: "center",
            color: colors.textMuted,
          }}
        >
          Ready for your own procedures?{" "}
          <Link to="/register">Create your team&rsquo;s account</Link>.
        </p>

        <div style={{ marginTop: 12, textAlign: "center" }}>
          <Link to="/login" style={{ color: colors.textMuted, fontSize: "0.85rem" }}>
            Sign in with an existing account
          </Link>
        </div>
      </div>
    </div>
  );
}
